import { FC, useRef, useState } from "react";
import Markdown from "react-markdown";

import SectionHeader from "../Layout/SectionHeader";

import classes from "./Upload.module.css";
import BottomSticky from "../UI/BottomSticky";
import Button from "../UI/Button";
import { useDispatch, useSelector } from "react-redux";
import { artworkState, setArtwork } from "../../store/artwork";
import { useNavigate } from "react-router-dom";
import { userState } from "../../store/user";
import axios from "axios";
import { getLang, i18n } from "../../i18n";

const Upload: FC = () => {
  const lang = getLang();
  const t = i18n.upload[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);
  const [state, setState] = useState("ai");
  const artwork = useSelector(artworkState);
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const rightsRef = useRef<HTMLInputElement>(null);
  const legalRef = useRef<HTMLInputElement>(null);
  const [titleError, setTitleError] = useState(false);
  const [rightsError, setRightsError] = useState(false);
  const [legalError, setLegalError] = useState(false);
  const [screenIsLoading, setScreenIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const saveImageHandler = async () => {
    // Validation
    let hasErrors = false;
    if (titleRef.current?.value.trim().length === 0) {
      setTitleError(true);
      hasErrors = true;
    }
    if (!rightsRef.current?.checked) {
      setRightsError(true);
      hasErrors = true;
    }
    if (!legalRef.current?.checked) {
      setLegalError(true);
      hasErrors = true;
    }

    if (!hasErrors) {
      setScreenIsLoading(true);
      axios
        .post(
          `${API_URL}/artworks/save`,
          {
            imageAmazonS3URL: artwork.imageAmazonS3URL,
            aiMentorReview: artwork.aiMentorReview,
            aiMentorDescription: artwork.aiMentorDescription,
            category: artwork.category,
            tags: artwork.tags,
            id: artwork.id,
            slot: artwork.slot,
            title: titleRef.current?.value.trim(),
            description: descriptionRef.current?.value.trim(),
          },
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${user.sessionToken}`,
            },
          }
        )
        .then((response) => {
          dispatch(setArtwork({}));
          setScreenIsLoading(false);
          navigate(`/battle?slot=${artwork.slot}`);
        })
        .catch((err) => {
          console.log(err);
          setScreenIsLoading(false);
        });
    }
  };
  return (
    <>
      <SectionHeader
        label={t.sectionHeader.titleSlot(artwork.slot || "")}
        back=""
        cancel=""
      />

      <section className={classes.upload}>
        <div className="container">
          <div className={classes.preview}>
            <img src={artwork.imageAmazonS3URL} alt="" />
          </div>
        </div>

        <div className={classes.states}>
          {/* AI State */}
          {state === "ai" && (
            <div className={classes.state}>
              <div className="container">
                <div className={classes.rate}>
                  <h2>{t.aiState.category}</h2>
                </div>
                <div className={classes.ai}>{artwork.category}</div>

                <div className={classes.rate}>
                  <h2>{t.aiState.tags}</h2>
                  <div className={classes.points}>
                    {t.aiState.points(artwork.tags?.length || 0)}
                  </div>
                </div>

                <div className={classes.tags}>
                  {artwork.tags?.map((tag, index) => (
                    <div key={`tag-${index}`} className={classes.tag}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 15"
                        fill="none"
                      >
                        <path
                          d="M3.14697 8.20187L5.80826 10.5882L12.8529 4.41177"
                          stroke="#868686"
                          strokeWidth="1.11111"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {tag}
                    </div>
                  ))}
                </div>

                {artwork.aiMentorReview && (
                  <div className={classes.rate}>
                    <h2>{t.aiState.aiMentorReview}</h2>
                    <div className={classes.points}>
                      {(artwork.aiMentorReview * 10).toFixed(1)} / 10
                    </div>
                  </div>
                )}

                {artwork.aiMentorDescription && (
                  <div className={classes.ai}>
                    <Markdown>{artwork.aiMentorDescription}</Markdown>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form State */}
          {state === "form" && (
            <div className={classes.state}>
              <div className="container">
                <div
                  className={`${classes.input} ${
                    titleError ? classes.error : ""
                  }`}
                >
                  <input
                    ref={titleRef}
                    type="text"
                    placeholder={t.formState.titlePlaceholder}
                    maxLength={80}
                    onChange={() => setTitleError(false)}
                  />
                </div>

                <div className={classes.input}>
                  <textarea
                    ref={descriptionRef}
                    placeholder={t.formState.descriptionPlaceholder}
                    maxLength={200}
                  />
                </div>

                <div
                  className={`${classes.checkbox} ${
                    rightsError ? classes.error : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    id="rights"
                    ref={rightsRef}
                    onChange={() => setRightsError(false)}
                  />
                  <label htmlFor="rights">{t.formState.confirmRights}</label>
                </div>

                <div
                  className={`${classes.checkbox} ${
                    legalError ? classes.error : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    id="legal"
                    ref={legalRef}
                    onChange={() => setLegalError(false)}
                  />
                  <label htmlFor="legal">{t.formState.agreeLegal}</label>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <BottomSticky>
        <div className="container">
          <Button
            label={state === "ai" ? t.formState.nextBtn : t.formState.saveBtn}
            disabled={state === "form" && screenIsLoading}
            onClick={() =>
              state === "ai" ? setState("form") : saveImageHandler()
            }
          />
        </div>
      </BottomSticky>
    </>
  );
};

export default Upload;
